import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  Briefcase,
  Eye,
  ArrowRight,
  Play,
  FileCheck,
  Archive
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientProjectsV3() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectsWithNotifications, setProjectsWithNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    fetchProjects();
    fetchProjectsWithNotifications();
  }, [token]);

  const fetchProjectsWithNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/projects-with-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectsWithNotifications(res.data?.project_ids || []);
    } catch (error) {
      console.error("Error fetching projects with notifications:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/projects-v3/client/my-projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProjects(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Kunne ikke laste prosjekter");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending_holding_review: { label: t('status.pendingApproval'), color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
      approved_pending_client_sign: { label: t('contracts.signContract'), color: "bg-blue-500/20 text-blue-400", icon: AlertTriangle },
      live: { label: t('status.searchingTeam'), color: "bg-green-500/20 text-green-400", icon: Users },
      has_applications: { label: t('status.hasApplications'), color: "bg-purple-500/20 text-purple-400", icon: Users },
      team_selected: { label: t('status.teamSelected'), color: "bg-cyan-500/20 text-cyan-400", icon: CheckCircle },
      team_contracted: { label: t('status.contractSigned'), color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle },
      in_progress: { label: t('status.inProgress'), color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle },
      completed: { label: t('status.completed'), color: "bg-gray-500/20 text-gray-400", icon: CheckCircle },
      rejected: { label: t('status.rejected'), color: "bg-red-500/20 text-red-400", icon: AlertTriangle }
    };
    return configs[status] || { label: status, color: "bg-gray-500/20 text-gray-400", icon: Clock };
  };

  const getActionButton = (project) => {
    if (project.status === "approved_pending_client_sign") {
      return (
        <Link to={`/client/projects-v3/${project.project_id}/sign`}>
          <Button className="bg-blue-500 hover:bg-blue-600 text-white">
            {t('contracts.signContract')}
          </Button>
        </Link>
      );
    }
    if (["live", "has_applications"].includes(project.status) && project.applications?.length > 0) {
      return (
        <Link to={`/client/projects-v3/${project.project_id}`}>
          <Button className="bg-purple-500 hover:bg-purple-600 text-white">
            {t('applications.viewApplications')} ({project.applications.length})
          </Button>
        </Link>
      );
    }
    return (
      <Link to={`/client/projects-v3/${project.project_id}`}>
        <Button variant="outline" className="border-[#2a2a4a] text-white hover:bg-[#2a2a4a]">
          <Eye className="w-4 h-4 mr-2" />
          {t('common.viewDetails')}
        </Button>
      </Link>
    );
  };

  // Filter projects based on active tab
  const getFilteredProjects = () => {
    switch (activeTab) {
      case "pending":
        // Pending approval or signature
        return projects.filter(p => 
          ["pending_holding_review", "approved_pending_client_sign"].includes(p.status)
        );
      case "live":
        // Live/searching for teams
        return projects.filter(p => 
          ["live", "has_applications", "team_selected"].includes(p.status)
        );
      case "active":
        // In progress with team
        return projects.filter(p => 
          ["team_contracted", "in_progress"].includes(p.status)
        );
      case "completed":
        // Completed projects
        return projects.filter(p => p.status === "completed");
      default:
        return projects;
    }
  };

  const filteredProjects = getFilteredProjects();

  // Count for each tab
  const counts = {
    all: projects.length,
    pending: projects.filter(p => ["pending_holding_review", "approved_pending_client_sign"].includes(p.status)).length,
    live: projects.filter(p => ["live", "has_applications", "team_selected"].includes(p.status)).length,
    active: projects.filter(p => ["team_contracted", "in_progress"].includes(p.status)).length,
    completed: projects.filter(p => p.status === "completed").length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="client-projects-v3">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('projects.myProjects')}</h1>
            <p className="text-gray-400 mt-1">{t('projects.manageYourProjects')}</p>
          </div>
          <Link to="/client/request-project">
            <Button className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
              <Plus className="w-4 h-4 mr-2" />
              {t('nav.requestQuote')}
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">{projects.length}</p>
              <p className="text-gray-400 text-sm">{t('common.total')}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">
                {counts.pending}
              </p>
              <p className="text-gray-400 text-sm">{t('common.pending')}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">
                {counts.active}
              </p>
              <p className="text-gray-400 text-sm">{t('common.active')}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#23F7DD]">
                {formatCurrency(projects.reduce((sum, p) => sum + (p.final_budget || p.proposed_budget || 0), 0))}
              </p>
              <p className="text-gray-400 text-sm">{t('common.totalValue')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Filter */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#1a1a2e] border border-[#2a2a4a] p-1 h-auto flex-wrap">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black px-4 py-2"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              {t('common.all')} ({counts.all})
            </TabsTrigger>
            <TabsTrigger 
              value="pending" 
              className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black px-4 py-2"
            >
              <Clock className="w-4 h-4 mr-2" />
              {t('common.pending')} ({counts.pending})
            </TabsTrigger>
            <TabsTrigger 
              value="live" 
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white px-4 py-2"
            >
              <Play className="w-4 h-4 mr-2" />
              {t('status.live') || 'Live'} ({counts.live})
            </TabsTrigger>
            <TabsTrigger 
              value="active" 
              className="data-[state=active]:bg-green-500 data-[state=active]:text-black px-4 py-2"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('status.inProgress')} ({counts.active})
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="data-[state=active]:bg-gray-500 data-[state=active]:text-white px-4 py-2"
            >
              <Archive className="w-4 h-4 mr-2" />
              {t('status.completed')} ({counts.completed})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Notifications section removed */}

        {/* Projects List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-400 mt-4">{t('common.loading')}...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardContent className="p-12 text-center">
                <Briefcase className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                {projects.length === 0 ? (
                  <>
                    <h3 className="text-white font-medium mb-2">{t('projects.noProjects')}</h3>
                    <p className="text-gray-400 mb-6">{t('projects.startFirstProject')}</p>
                    <Link to="/client/request-project">
                      <Button className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('projects.createProject')}
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <h3 className="text-white font-medium mb-2">{t('projects.noProjectsInCategory') || 'Ingen prosjekter i denne kategorien'}</h3>
                    <p className="text-gray-400">{t('projects.tryAnotherTab') || 'Prøv en annen fane'}</p>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredProjects.map((project) => {
              const statusConfig = getStatusConfig(project.status);
              const StatusIcon = statusConfig.icon;
              const hasNotification = projectsWithNotifications.includes(project.project_id);
              
              return (
                <Card 
                  key={project.project_id} 
                  className={`bg-[#1a1a2e] transition-all ${
                    hasNotification 
                      ? "border-2 border-[#23F7DD] shadow-[0_0_15px_rgba(35,247,221,0.3)] animate-pulse-slow" 
                      : "border-[#2a2a4a] hover:border-[#23F7DD]/50"
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Left */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${statusConfig.color} flex items-center justify-center flex-shrink-0 relative`}>
                            <StatusIcon className="w-5 h-5" />
                            {hasNotification && (
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-white font-semibold truncate">{project.title}</h3>
                              <Badge className={`${statusConfig.color} border-0`}>{statusConfig.label}</Badge>
                              {hasNotification && (
                                <Badge className="bg-red-500/20 text-red-400 border-0 animate-pulse">
                                  {t('notifications.newActivity')}
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{project.location_city}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                <span>{project.crew_size} {t('projects.workers').toLowerCase()}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>{formatDate(project.start_date)}</span>
                              </div>
                              {project.applications?.length > 0 && (
                                <Badge className="bg-purple-500/20 text-purple-400 border-0">
                                  {project.applications.length} {t('nav.applications').toLowerCase()}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right */}
                      <div className="flex items-center gap-4 lg:flex-col lg:items-end">
                        <div className="text-right">
                          <p className="text-[#23F7DD] font-bold text-xl">
                            {formatCurrency(project.final_budget || project.proposed_budget)}
                          </p>
                          <p className="text-gray-500 text-xs">{formatDate(project.created_at)}</p>
                        </div>
                        {getActionButton(project)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
